import client from './client';

export interface RuleChainNode {
  id: string;
  type: 'source' | 'transform' | 'filter' | 'enrich' | 'action' | 'route' | 'debug' | 'external' | 'flow';
  kind?: string;
  name?: string;
  config: Record<string, unknown>;
  position?: { x: number; y: number };
  width?: number;
  height?: number;
  errorPolicy?: 'stop' | 'skip' | 'fallback';
  retry?: { maxAttempts?: number; backoffMs?: number };
  timeoutMs?: number;
}

export type RuleChainNodeType = RuleChainNode['type'];

export interface RuleChainEdge {
  id?: string;
  from: string;
  to: string;
  fromPort?: string;
  condition?: string;
  label?: string;
}

export interface RuleChainGraph {
  nodes: RuleChainNode[];
  edges: RuleChainEdge[];
}

export interface RuleChainItem {
  id: string;
  organizationId?: string | null;
  name: string;
  description?: string | null;
  enabled: boolean;
  currentVersionId?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface RuleChainVersionItem {
  id: string;
  chainId: string;
  version: number;
  graph: RuleChainGraph;
  status: string;
  createdAt?: string;
  publishedAt?: string | null;
}

export interface RuleChainRunItem {
  id: string;
  chainId: string;
  versionId?: string | null;
  triggerType: string;
  status: string;
  durationMs: number;
  error?: string | null;
  createdAt: string;
}

export interface RuleChainNodeResult {
  nodeId: string;
  type: string;
  kind?: string;
  status: 'success' | 'skipped' | 'failed';
  input?: unknown;
  output?: unknown;
  error?: string;
  durationMs: number;
  attempts?: number;
  branch?: string;
}

export interface RuleChainTestResult {
  ok: boolean;
  status: 'success' | 'failed';
  nodeResults: RuleChainNodeResult[];
  output?: unknown;
  error?: string;
  durationMs: number;
}

export const fetchRuleChains = async (): Promise<{ items: RuleChainItem[] }> =>
  (await client.get('/rule-chains')).data;

export const createRuleChain = async (data: { name: string; description?: string }): Promise<RuleChainItem> =>
  (await client.post('/rule-chains', data)).data;

export const fetchRuleChain = async (id: string): Promise<RuleChainItem & { versions: RuleChainVersionItem[] }> =>
  (await client.get(`/rule-chains/${id}`)).data;

export const updateRuleChain = async (id: string, data: Partial<RuleChainItem>): Promise<RuleChainItem> =>
  (await client.patch(`/rule-chains/${id}`, data)).data;

export const deleteRuleChain = async (id: string): Promise<{ deleted: boolean }> =>
  (await client.delete(`/rule-chains/${id}`)).data;

export const saveRuleChainDraft = async (id: string, graph: RuleChainGraph): Promise<RuleChainVersionItem> =>
  (await client.put(`/rule-chains/${id}/draft`, { graph })).data;

export const publishRuleChain = async (id: string): Promise<{ published: boolean; version: number }> =>
  (await client.post(`/rule-chains/${id}/publish`, {})).data;

export const rollbackRuleChain = async (id: string, versionId: string): Promise<{ rolledBack: boolean; version: number }> =>
  (await client.post(`/rule-chains/${id}/rollback`, { versionId })).data;

export const testRuleChain = async (id: string, payload: unknown): Promise<RuleChainTestResult> =>
  (await client.post(`/rule-chains/${id}/test`, { payload })).data;

export interface RuleChainAiDraftResult {
  ok: boolean;
  name?: string;
  description?: string;
  graph?: RuleChainGraph;
  explanation?: string;
  warnings?: string[];
  errors?: string[];
  attempts?: number;
  model?: string;
  testResult?: RuleChainTestResult;
}

export const aiDraftRuleChain = async (body: { requirement: string; payload?: unknown }): Promise<RuleChainAiDraftResult> =>
  (await client.post('/rule-chains/ai-draft', body, { timeout: 120000 })).data;

export const fetchRuleChainRuns = async (id: string): Promise<{ items: RuleChainRunItem[] }> =>
  (await client.get(`/rule-chains/${id}/runs`)).data;

export const fetchRuleChainRunDetail = async (runId: string): Promise<{
  run: RuleChainRunItem;
  chain: { id: string; name: string };
  logs: Array<{ nodeId: string; nodeType: string; status: string; input?: unknown; output?: unknown; error?: string; durationMs: number }>;
}> => (await client.get(`/rule-chains/runs/${runId}`)).data;
